import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import { Link, NavLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import { movdTokens } from '../design-system/movdTokens.js';
import { IconButton, Drawer, List, ListItem, ListItemText, useMediaQuery, useTheme } from '@mui/material';
import { Menu as MenuIcon, Close as CloseIcon } from '@mui/icons-material';
import MovdLogo from './MovdLogo.jsx';
import React, { useState } from 'react';

export default function Navbar() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  const navigationItems = [
    { label: 'How it works', path: '/how-it-works' },
    { label: 'FAQ', path: '/faq' },
    { label: 'Contact', path: '/contact' },
  ];

  return (
    <>
      <a href="#main-content" className="skip-to-content-link" tabIndex={0} style={{position:'absolute',left:0,top:0,background: movdTokens.color.surface.base,color: movdTokens.color.content.primary,padding:'8px 16px',zIndex:2000,transform:'translateY(-120%)',transition:'transform 0.2s',fontWeight:700}} onFocus={e=>e.target.style.transform='none'} onBlur={e=>e.target.style.transform='translateY(-120%)'}>Skip to main content</a>
      
      <AppBar 
        position="fixed" 
        sx={{ 
          background: movdTokens.color.surface.base,
          color: movdTokens.color.content.primary,
          borderBottom: `1px solid ${movdTokens.color.border.soft}`,
          boxShadow: 'none',
          width: '100%',
          maxWidth: '100vw',
          overflow: 'hidden'
        }}
      >
        <Toolbar 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            minHeight: { xs: 64, md: 72 },
            px: { xs: 2, md: 5 },
            width: '100%', maxWidth: 1180, mx: 'auto', overflow: 'hidden'
          }}
        >
          {/* Logo */}
          <Box sx={{ flexShrink: 0 }}>
            <MovdLogo size={isMobile ? "small" : "medium"} variant="full" color="white" />
          </Box>

          {/* Desktop Navigation */}
          {!isMobile && (
            <>
              <Box component="nav" aria-label="Primary" sx={{ display: 'flex', gap: 1.5, ml: 'auto' }}>
                {navigationItems.map((item) => (
                  <Button
                    key={item.path}
                    color="inherit"
                    component={NavLink}
                    to={item.path}
                    sx={{
                      ...navBtnSx,
                      '&.active': { color: '#008d7b' }
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
                <Button
                  component={Link}
                  to="/?start=1"
                  variant="contained"
                  sx={{
                    ml: 0.75,
                    px: 1.5,
                    py: 0.85,
                    borderRadius: 1,
                    bgcolor: '#008d7b',
                    color: '#FFFFFF',
                    fontWeight: 800,
                    fontSize: '0.82rem',
                    boxShadow: 'none',
                    '&:hover': { bgcolor: '#007566', boxShadow: 'none' },
                  }}
                >
                  Start your move
                </Button>
                <Button
                  component={Link}
                  to="/mover-portal/dashboard"
                  sx={{
                    ml: 0.25,
                    px: 1,
                    py: 0.85,
                    color: '#173057',
                    fontWeight: 700,
                    fontSize: '0.82rem',
                    boxShadow: 'none',
                    '&:hover': { color: '#008d7b', boxShadow: 'none' },
                  }}
                >
                  Mover login
                </Button>
              </Box>
            </>
          )}

          {/* Mobile Menu Button */}
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="menu"
              onClick={handleMobileMenuToggle}
              sx={{ ml: 'auto' }}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer Menu */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={handleMobileMenuClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: 280,
            bgcolor: '#1E293B',
            color: 'white',
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <MovdLogo size="small" variant="full" color="white" />
            <IconButton color="inherit" onClick={handleMobileMenuClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          
          <List sx={{ pt: 0 }}>
            {navigationItems.map((item) => (
              <ListItem 
                key={item.path}
                component={Link} 
                to={item.path} 
                onClick={handleMobileMenuClose}
                sx={{ 
                  color: 'inherit', 
                  textDecoration: 'none',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                  borderRadius: 1,
                  mb: 0.5
                }}
              >
                <ListItemText primary={item.label} />
              </ListItem>
            ))}
            <ListItem
              component={Link}
              to="/?start=1"
              onClick={handleMobileMenuClose}
              sx={{
                mt: 1.25,
                bgcolor: '#008d7b',
                color: '#FFFFFF',
                textDecoration: 'none',
                borderRadius: 1,
                '&:hover': { bgcolor: '#007566' },
              }}
            >
              <ListItemText primary="Start your move" primaryTypographyProps={{ fontWeight: 800 }} />
            </ListItem>
            <ListItem
              component={Link}
              to="/mover-portal/dashboard"
              onClick={handleMobileMenuClose}
              sx={{
                mt: 0.5,
                color: '#E1F7F3',
                textDecoration: 'none',
                borderRadius: 1,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
              }}
            >
              <ListItemText primary="Mover login" primaryTypographyProps={{ fontWeight: 800 }} />
            </ListItem>
          </List>
        </Box>
      </Drawer>
    </>
  );
}

const navBtnSx = {
  fontWeight: 700,
  fontSize: '0.9rem',
  letterSpacing: 0,
  px: 1.25,
  py: 1,
  borderRadius: 0,
  minWidth: 'unset',
  background: 'none',
  backgroundColor: 'transparent !important',
  boxShadow: 'none !important',
  filter: 'none !important',
  outline: 'none !important',
  color: '#173057',
  transition: 'color 0.2s',
  '&:hover': {
    color: '#008d7b',
    background: 'none',
    backgroundColor: 'transparent !important',
    textDecoration: 'underline',
    boxShadow: 'none !important',
    filter: 'none !important',
    outline: 'none !important',
  },
};
